import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { register } from '../../services/api';
import { useTheme } from '../ThemeContext'; 
import api from '../../services/api';
import './UserCompanyRegistration.css';

export default function RegisterPage() {
  const { isDark } = useTheme(); 
  const [isCompany, setIsCompany] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // ==========================================================
  // ⚡ INTÉGRATION POP-UP GOOGLE POUR L'INSCRIPTION
  // ==========================================================
  const handleGoogleCredentialResponse = useCallback(async (googleResponse) => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post('/auth/google/', { 
        role: isCompany ? 'COMPANY' : 'CANDIDATE', 
        token: googleResponse.credential 
      });

      if (response.data.status || response.data.user) {
        window.location.href = '/dashboard'; 
      }
    } catch (err) {
      console.error("Erreur Google Registration backend :", err);
      const backendMessage = err.response?.data?.details || err.response?.data?.detail;
      setError(backendMessage || "Le backend a refusé l'inscription via Google.");
    } finally {
      setLoading(false);
    }
  }, [isCompany]);

  useEffect(() => {
    /* global google */
    if (window.google) {
      google.accounts.id.initialize({
        // ⚠️ Pense à coller ton vrai Client ID ici aussi !
        client_id: "TON_CLIENT_ID_GOOGLE_ICI.apps.googleusercontent.com", 
        callback: handleGoogleCredentialResponse,
      });

      google.accounts.id.renderButton(
        document.getElementById("google-register-button-div"),
        { 
          theme: isDark ? "dark" : "outline", 
          size: "large",
          text: "signup_with",
          shape: "rectangular",
          width: "100%"
        }
      );
    }
  }, [isDark, handleGoogleCredentialResponse]);

  // ==========================================================
  // L'INSCRIPTION FORMULAIRE CLASSIQUE
  // ==========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    // ✅ CORRECTION : loading(true) transformé en setLoading(true)
    setLoading(true);

    const payload = {
      email: formData.email,
      password: formData.password,
      role: isCompany ? 'COMPANY' : 'CANDIDATE',
      firstName: formData.firstName,
      secondName: formData.secondName,
      companyName: isCompany ? formData.companyName : '',
    };

    try {
      const response = await register(payload);

      if (response.status === true) {
        setSuccess(true);
        setRegisteredEmail(formData.email);
      } else {
        setError(response.details || 'Registration failed');
      }
    } catch (err) {
      const errorDetails = err.response?.data?.details || err.response?.data?.error;

      if (err.response?.status === 409) {
        setError('An account with this email already exists. Please login instead.');
      } else {
        setError(errorDetails || 'Server error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`page-bg ${!isDark ? 'light' : ''}`}>
        <div className="card-register success-card">
          <div className="success-icon">&#10003;</div>
          <h2 className="title-create">Registration Successful!</h2>
          <p className="success-message">
            We've sent an activation link to <strong>{registeredEmail}</strong>.
            Please check your email and click the link to activate your account.
          </p>
          <div className="success-actions">
            <Link to="/login" className="btn-register-primary">
              Go to Login
            </Link>
            <button
              type="button"
              className="btn-register-secondary"
              onClick={() => {
                setSuccess(false);
                setFormData({
                  firstName: '',
                  secondName: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                  companyName: '',
                });
              }}
            >
              Register Another Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-bg ${!isDark ? 'light' : ''}`}>
      <div className="card-register">
        <div className="logo-box-register">
          <div className="logo-icon" />
          <div className="logo-text">SkillHunt</div>
        </div>

        <div className="title-create">Create Your Account</div>

        <div className="role-switch">
          <button
            type="button"
            className={`role ${!isCompany ? 'active' : ''}`}
            onClick={() => setIsCompany(false)}
          >
            Candidate
          </button>
          <button
            type="button"
            className={`role ${isCompany ? 'active' : ''}`}
            onClick={() => setIsCompany(true)}
          >
            Company
          </button>
        </div>

        <form className="form" onSubmit={handleSubmit}>
          {!isCompany ? (
            <>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="secondName"
                placeholder="Second Name"
                value={formData.secondName}
                onChange={handleChange}
                required
              />
            </>
          ) : (
            <input
              type="text"
              name="companyName"
              placeholder="Company Name"
              value={formData.companyName}
              onChange={handleChange}
              required
            />
          )}

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Password (min. 8 characters)"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />

          {error && (
            <p className="error-message" style={{ color: !isDark ? '#dc2626' : '#ff4d4d', fontSize: '14px', marginBottom: '10px' }}>
              {error}
            </p>
          )}

          <button type="submit" className="btn-register-primary" disabled={loading}>
            {loading ? 'Processing...' : 'Register'}
          </button>
        </form>

        <div className="divider-register">
          <p>or</p>
        </div>

        {/* ✅ Remplacement par l'ancre du bouton Google Pop-up officiel */}
        <div id="google-register-button-div" style={{ width: '100%', minHeight: '44px', marginBottom: '15px' }}></div>

        <p className="footer-text">
          Already have an account? <Link to="/login" style={{ color: !isDark ? '#2563EB' : '#a855f7' }}>Login</Link>
        </p>
      </div>
    </div>
  );
}